
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Student } from '@/services/db/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/utils/formatters';

interface StudentPaymentSectionProps {
  student: Student;
}

const StudentPaymentSection = ({ student }: StudentPaymentSectionProps) => {
  const navigate = useNavigate();

  const handleProcessPayment = () => {
    navigate(`/students/${student.id}/payment`);
  };

  const getTotalPendingAmount = () => {
    return student.programs.reduce((total, program) => {
      if (program.feeStatus === 'pending') {
        return total + (program.fee || 0);
      } else if (program.feeStatus === 'partial') {
        return total + ((program.fee || 0) / 2);
      }
      return total;
    }, 0);
  };

  const getNextPaymentDate = () => {
    const upcomingPayments = student.programs
      .filter(p => p.nextPaymentDate && (p.feeStatus === 'pending' || p.feeStatus === 'partial'))
      .sort((a, b) => {
        const dateA = new Date(a.nextPaymentDate || '');
        const dateB = new Date(b.nextPaymentDate || '');
        return dateA.getTime() - dateB.getTime();
      });

    return upcomingPayments[0]?.nextPaymentDate;
  };

  const nextPaymentDate = getNextPaymentDate();
  const pendingAmount = getTotalPendingAmount();

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <Wallet className="h-5 w-5 mr-2" />
          Payment Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-2">
            <div>
              <p className="text-sm text-muted-foreground">Pending Amount</p>
              <p className="text-2xl font-bold flex items-center">
                AFN {formatCurrency(pendingAmount)}
              </p>
            </div>
            <Button 
              onClick={handleProcessPayment}
              disabled={pendingAmount === 0}
              className="bg-primary hover:bg-primary/90"
              size="sm"
            >
              Process Payment
            </Button>
          </div>

          {nextPaymentDate && (
            <div className="flex items-center gap-2 bg-muted/50 p-3 rounded-lg">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Next Payment Due</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(nextPaymentDate), 'PPP')}
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">Program Fee Status</p>
            <div className="grid gap-2">
              {student.programs.map((program, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{program.title}</p>
                    <p className="text-sm text-muted-foreground">AFN {formatCurrency(program.fee || 0)}</p>
                  </div>
                  <Badge variant={
                    program.feeStatus === 'paid' ? 'default' :
                    program.feeStatus === 'partial' ? 'secondary' : 'destructive'
                  }>
                    {program.feeStatus}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentPaymentSection;
